import { createAsyncThunk, createSlice, PayloadAction, isAnyOf } from '@reduxjs/toolkit';
import { okaoAxios } from '../../helpers/axios';
import { RootState, store } from '../../redux/store';
import { MenuRequest, DishRequest } from './types'
import { IMenuLoadRequest, NewMenuRequest } from '../MenuLoadRequest/types';
import { MenuScanResultDTO, MenuSaveRequestDTO, MenuSaveResponseDTO, MenuScanDish, ScannedDishDTO } from './scan-types';
import { AlertColor } from '@mui/material/Alert';

export interface IMenuSlice {
  status: string
  pageSize: number
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  isUploadingImage: boolean
  uploadImageError: string | undefined
  scanResult: MenuScanResultDTO | null
}

const initialState: IMenuSlice = {
  status: 'idle',
  pageSize: 100,
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  isUploadingImage: false,
  uploadImageError: undefined,
  scanResult: null
};

export const addMenu = createAsyncThunk('api/addMenu', async (nmr: IMenuLoadRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/add-menu`, nmr));
  return response;
})

export const addNewMenu = createAsyncThunk('api/addNewMenu', async (nmr: NewMenuRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/add-new-menu`, nmr));
  return response;
})

export const updateMenu = createAsyncThunk('api/updateMenu', async (menu: MenuRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).put(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/${menu.menuId}`, menu));
  return response;
})

export const saveDish = createAsyncThunk('api/saveDish', async (dish: DishRequest) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/save-dish`, dish));
  return response;
})

// Upload dish image
export interface ImageUploadResponse {
  imageUrl: string;
}

export const uploadDishImage = createAsyncThunk<{ dishId: string; imageUrl: string }, { dishId: string; file: File }>(
  'api/uploadDishImage',
  async ({ dishId, file }) => {
    const auth = store.getState().secure.accessToken ?? '';
    const formData = new FormData();
    formData.append('file', file);
    const response = await okaoAxios(auth).post<ImageUploadResponse>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/dish/${dishId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return { dishId, imageUrl: response.data.imageUrl };
  }
);

// Delete dish image
export const deleteDishImage = createAsyncThunk<{ dishId: string }, { dishId: string }>(
  'api/deleteDishImage',
  async ({ dishId }) => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).delete(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/dish/${dishId}`,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return { dishId };
  }
);

export const deleteDish = createAsyncThunk('api/deleteDish', async (dishId: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).delete(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/dish/${dishId}`));
  return response;
})

export const publishMenu = createAsyncThunk('api/publishMenu', async (menuId: string) => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await (await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/publish/${menuId}`));
  return response;
})

export const deleteMenu = createAsyncThunk<Record<string, never>, string>(
  'api/deleteMenu',
  async (menuId: string) => {
    const auth = store.getState().secure.accessToken ?? '';
    await okaoAxios(auth).delete(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/menu/${menuId}`
    );
    return {};
  }
)

// Upload & Scan Menu
export const uploadAndScanMenu = createAsyncThunk<MenuScanResultDTO, FormData>(
  'api/uploadAndScanMenu',
  async (formData: FormData) => {
    const auth = store.getState().secure.accessToken ?? '';
    const url = `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/menus/scan/upload`;
    
    try {
      const response = await okaoAxios(auth).post<MenuScanResultDTO>(
        url,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Menu scan upload API error:', {
        url,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        formDataKeys: Array.from(formData.keys())
      });
      throw error;
    }
  }
);

// Get Scan Result (optional - for async scanning)
export const getScanResult = createAsyncThunk<MenuScanResultDTO, string>(
  'api/getScanResult',
  async (scanId: string) => {
    if (!scanId || scanId.trim() === '') {
      console.warn('Cannot fetch scan result: scanId is null or empty');
      throw new Error('Scan ID is required');
    }

    const auth = store.getState().secure.accessToken ?? '';
    const url = `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/menus/scan/${scanId}`;
    
    try {
      const response = await okaoAxios(auth).get<MenuScanResultDTO>(url);
      return response.data;
    } catch (error: any) {
      console.error('Menu scan get result API error:', {
        url,
        scanId,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      throw error;
    }
  }
);

// Save Menu from Scan
export const saveMenuFromScan = createAsyncThunk<MenuSaveResponseDTO, { scanId: string; request: MenuSaveRequestDTO }>(
  'api/saveMenuFromScan',
  async ({ scanId, request }) => {
    if (!scanId || scanId.trim() === '') {
      console.warn('Cannot save menu from scan: scanId is null or empty');
      throw new Error('Scan ID is required');
    }

    const auth = store.getState().secure.accessToken ?? '';
    const url = `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/menus/scan/${scanId}/save`;
    
    try {
      const response = await okaoAxios(auth).post<MenuSaveResponseDTO>(url, request);
      return response.data;
    } catch (error: any) {
      console.error('Menu scan save API error:', {
        url,
        scanId,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        requestPayload: request
      });
      throw error;
    }
  }
);

export const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
      setPageSize: (state, action: PayloadAction<number>) => {
        state.pageSize = action.payload
      },
      setOpen: (state, action: PayloadAction<boolean>) => {
        state.shouldOpen = action.payload
      }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadAndScanMenu.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(uploadAndScanMenu.fulfilled, (state, action) => {
        const result = action.payload;
        
        // Store the result as-is (backend now returns flat dishes array)
        // The wizard will group dishes by section
        state.scanResult = {
          ...result,
          dishes: result.dishes ?? [],
          // Keep sections for backward compatibility if present
          sections: result.sections ?? []
        };
        state.status = 'idle';
        
        // Log for debugging
        console.log('uploadAndScanMenu.fulfilled payload:', result);
        console.log('storing dishes count:', result.dishes?.length ?? 0);
      })
      .addCase(uploadAndScanMenu.rejected, (state, action) => {
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'Failed to upload and scan menu'
        state.severity = 'error'
      })
      .addCase(saveMenuFromScan.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(saveMenuFromScan.fulfilled, (state, action) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.message = action.payload.message ?? `Menu saved successfully. ${action.payload.dishesSaved ?? 0} dishes saved.`
        state.severity = 'success'
        // Clear scan result after successful save
        state.scanResult = null
      })
      .addCase(saveMenuFromScan.rejected, (state, action) => {
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'Failed to save menu'
        state.severity = 'error'
      })
      // Dish image upload handlers
      .addCase(uploadDishImage.pending, (state) => {
        state.isUploadingImage = true
        state.uploadImageError = undefined
        state.status = 'loading'
      })
      .addCase(uploadDishImage.fulfilled, (state, action) => {
        state.isUploadingImage = false
        state.uploadImageError = undefined
        state.status = 'idle'
        state.shouldOpen = true
        state.message = 'Dish image uploaded successfully'
        state.severity = 'success'
        // Note: Actual dish.imageUrl update happens in restaurant slice
        // since dishes are nested in restaurant.menus[].dishes[]
      })
      .addCase(uploadDishImage.rejected, (state, action) => {
        state.isUploadingImage = false
        state.uploadImageError = action.error.message ?? 'Failed to upload dish image'
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'Failed to upload dish image'
        state.severity = 'error'
      })
      // Dish image delete handlers
      .addCase(deleteDishImage.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(deleteDishImage.fulfilled, (state) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.message = 'Dish image removed successfully'
        state.severity = 'success'
        // Note: Actual dish.imageUrl update happens in restaurant slice
        // since dishes are nested in restaurant.menus[].dishes[]
      })
      .addCase(deleteDishImage.rejected, (state, action) => {
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'Failed to delete dish image'
        state.severity = 'error'
      })
      // Delete menu handlers
      .addCase(deleteMenu.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(deleteMenu.fulfilled, (state) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.message = 'Menu deleted successfully'
        state.severity = 'success'
      })
      .addCase(deleteMenu.rejected, (state, action) => {
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'Failed to delete menu'
        state.severity = 'error'
      })
      .addMatcher(isAnyOf(addNewMenu.fulfilled, updateMenu.fulfilled, saveDish.fulfilled, publishMenu.fulfilled), (state) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.message = 'Success'
        state.severity = 'success'
      })
      .addMatcher(isAnyOf(addNewMenu.rejected, updateMenu.rejected, saveDish.rejected, publishMenu.rejected, deleteMenu.rejected), (state, action) => {
        state.status = 'error'
        state.shouldOpen = true
        state.message = action.error.message ?? 'An error occurred'
        state.severity = 'error'
      })
  }
});

export const selectPageSize = (state: RootState): number => state.menu.pageSize
export const selectIsLoading = (state: RootState): boolean => state.menu.status === "loading"
export const selectShouldOpen = (state: RootState): boolean => state.menu.shouldOpen
export const selectMessage = (state: RootState): string | undefined => state.menu.message
export const selectSeverity = (state: RootState): AlertColor | undefined => state.menu.severity
export const selectIsUploadingImage = (state: RootState): boolean => state.menu.isUploadingImage
export const selectUploadImageError = (state: RootState): string | undefined => state.menu.uploadImageError
export const selectMenuScanResult = (state: RootState): MenuScanResultDTO | null => state.menu.scanResult
export const selectScannedDishes = (state: RootState): MenuScanDish[] => {
  const scanResult = state.menu.scanResult;
  if (!scanResult) return [];
  // Backend now returns flat dishes array directly
  if (scanResult.dishes && Array.isArray(scanResult.dishes)) {
    return scanResult.dishes.map((dish: ScannedDishDTO) => ({
      name: dish.name ?? '',
      description: dish.description,
      price: typeof dish.price === 'number' ? String(dish.price) : (dish.price ?? '')
    }));
  }
  // Fallback to sections format for backward compatibility
  return scanResult.sections?.flatMap(section => section.dishes ?? []) ?? [];
}
export const selectScanId = (state: RootState): string | null => state.menu.scanResult?.scanId ?? null

export const { setPageSize, setOpen } = menuSlice.actions;
export default menuSlice.reducer
